import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck,
  Store,
  ShoppingBag,
  Send,
  CheckCircle,
  X,
  ChevronRight,
  User,
  Mail,
  Phone,
  Building2,
} from "lucide-react";

const forms = {
  picker: {
    icon: ShoppingBag,
    title: "Shop for others",
    subtitle: "Bring your care and a good eye for the details.",
    chipBg: "bg-purple-100",
    chipText: "text-purple-800",
    fields: [
      {
        name: "name",
        label: "Full Name",
        type: "text",
        icon: User,
        required: true,
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        icon: Mail,
        required: true,
      },
      {
        name: "city",
        label: "City / ZIP Code",
        type: "text",
        icon: Building2,
        required: true,
      },
    ],
  },
  driver: {
    icon: Truck,
    title: "Become a Driver",
    subtitle: "Choose when you go online. Set your delivery rate.",
    chipBg: "bg-kwk-orange/12",
    chipText: "text-kwk-orange",
    fields: [
      {
        name: "name",
        label: "Full Name",
        type: "text",
        icon: User,
        required: true,
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        icon: Mail,
        required: true,
      },
      {
        name: "phone",
        label: "Phone",
        type: "tel",
        icon: Phone,
        required: true,
      },
      {
        name: "city",
        label: "City",
        type: "text",
        icon: Building2,
        required: true,
      },
    ],
  },
  seller: {
    icon: Store,
    title: "Become a Seller",
    subtitle: "Grow your business with delivery.",
    chipBg: "bg-kwk-sky/12",
    chipText: "text-kwk-sky",
    fields: [
      {
        name: "business",
        label: "Business Name",
        type: "text",
        icon: Building2,
        required: true,
      },
      {
        name: "name",
        label: "Your Name",
        type: "text",
        icon: User,
        required: true,
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        icon: Mail,
        required: true,
      },
      {
        name: "phone",
        label: "Phone",
        type: "tel",
        icon: Phone,
        required: true,
      },
      {
        name: "city",
        label: "City",
        type: "text",
        icon: Building2,
        required: true,
      },
    ],
  },
  buyer: {
    icon: ShoppingBag,
    title: "Shop your neighborhood",
    subtitle: "Find out what’s available where you live.",
    chipBg: "bg-kwk-green/12",
    chipText: "text-kwk-green",
    fields: [
      {
        name: "name",
        label: "Your Name",
        type: "text",
        icon: User,
        required: true,
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        icon: Mail,
        required: true,
      },
      {
        name: "zip",
        label: "ZIP Code",
        type: "text",
        icon: Building2,
        required: true,
      },
    ],
  },
};

// Where lead submissions go. Best practice for KwK: a POST /leads endpoint on
// kwk-api (persists to Mongo + emails the team). Configure via env at build time
// — falls back to ${VITE_API_URL}/leads. See .env.example.
const LEADS_ENDPOINT = import.meta.env.VITE_LEADS_ENDPOINT || "";
const CONTACT_EMAIL = "admin@kwknet.com";

export default function LeadCapture() {
  const location = useLocation();
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // A CTA elsewhere can deep-link here with a preselected role, e.g.
  // <Link to="/#join" state={{ role: 'driver' }}>. The Service-area ZIP check
  // also passes state.zip so the buyer form arrives prefilled.
  useEffect(() => {
    const role = location.state?.role;
    if (role && forms[role]) {
      const zip = location.state?.zip;
      setActiveForm(role);
      setSubmitted(false);
      setFormData(zip ? { zip: String(zip) } : {});
      setError("");
    }
  }, [location.state, location.key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (!LEADS_ENDPOINT) {
        const subject = `KwK ${activeForm} inquiry`;
        const body = Object.entries(formData)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        return;
      }
      const res = await fetch(LEADS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role: activeForm,
          source: "kwk-web",
          ...formData,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      console.error("Lead submission failed:", err);
      setError(
        "We couldn’t submit your details just now. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="join"
      className="relative py-24 overflow-hidden bg-wallpaper-warm"
    >
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-kwk-orange to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black text-kwk-ink mb-6">
            Find your place <span className="text-gradient">in KwK.</span>
          </h2>
          <p className="text-xl text-kwk-slate max-w-2xl mx-auto">
            Choose your role and tell us your area. Let’s find the right next
            step for you.
          </p>
        </motion.div>

        {/* Form Selection Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {Object.entries(forms).map(([key, form], index) => {
            const Icon = form.icon;
            return (
              <motion.button
                type="button"
                aria-pressed={activeForm === key}
                key={key}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  setActiveForm(key);
                  setSubmitted(false);
                  setFormData({});
                  setError("");
                }}
                className={`card p-6 text-left cursor-pointer transition-all hover:shadow-soft ${
                  activeForm === key
                    ? "ring-2 ring-kwk-orange"
                    : "hover:shadow-soft"
                }`}
              >
                <div
                  className={`w-16 h-16 rounded-xl ${form.chipBg} flex items-center justify-center mb-4`}
                >
                  <Icon className={`w-8 h-8 ${form.chipText}`} />
                </div>
                <h3 className="text-2xl font-bold text-kwk-ink mb-2">
                  {form.title}
                </h3>
                <p className="text-kwk-slate mb-4">{form.subtitle}</p>
                <div className="flex items-center text-kwk-orange font-bold">
                  Get Started <ChevronRight className="w-5 h-5" />
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Active Form */}
        <AnimatePresence mode="wait">
          {activeForm && !submitted && (
            <motion.div
              key={activeForm}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-2xl mx-auto"
            >
              <div className="card p-8 md:p-12 relative">
                <button
                  type="button"
                  aria-label="Close inquiry form"
                  onClick={() => {
                    setActiveForm(null);
                    setError("");
                  }}
                  className="absolute top-4 right-4 p-2 hover:bg-kwk-cream rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-kwk-slate" />
                </button>

                <div className="text-center mb-8">
                  {(() => {
                    const Icon = forms[activeForm].icon;
                    return (
                      <div
                        className={`w-16 h-16 rounded-xl ${forms[activeForm].chipBg} flex items-center justify-center mx-auto mb-4`}
                      >
                        <Icon
                          className={`w-8 h-8 ${forms[activeForm].chipText}`}
                        />
                      </div>
                    );
                  })()}
                  <h3 className="text-3xl font-bold text-kwk-ink">
                    {forms[activeForm].title}
                  </h3>
                  <p className="text-kwk-slate">{forms[activeForm].subtitle}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {forms[activeForm].fields.map((field) => {
                    const Icon = field.icon;
                    return (
                      <div key={field.name} className="relative">
                        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-kwk-slate" />
                        <input
                          aria-label={field.label}
                          autoComplete={
                            {
                              name: "name",
                              email: "email",
                              phone: "tel",
                              business: "organization",
                              city: "address-level2",
                              zip: "postal-code",
                            }[field.name]
                          }
                          type={field.type}
                          name={field.name}
                          placeholder={field.label}
                          required={field.required}
                          value={formData[field.name] ?? ""}
                          onChange={handleInputChange}
                          className="w-full bg-kwk-cream ring-1 ring-black/5 rounded-xl py-4 pl-12 pr-4 text-kwk-ink placeholder-kwk-slate focus:outline-none focus:ring-2 focus:ring-kwk-orange transition-all"
                        />
                      </div>
                    );
                  })}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-kwk-orange hover:bg-kwk-orange-dark text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-soft"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {LEADS_ENDPOINT ? "Send inquiry" : "Open email draft"}
                      </>
                    )}
                  </button>

                  {error && (
                    <p
                      role="alert"
                      className="text-center text-sm font-semibold text-red-600"
                    >
                      {error}
                    </p>
                  )}
                </form>

                <p className="text-center text-kwk-slate text-sm mt-4">
                  {LEADS_ENDPOINT ? (
                    "An inquiry is not an account approval or a guarantee of coverage."
                  ) : (
                    <>
                      This opens your email app; it does not submit or save your
                      details on this website. Send the draft yourself to
                      complete your inquiry. You can also email{" "}
                      <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                      </a>{" "}
                      directly.
                    </>
                  )}
                </p>
              </div>
            </motion.div>
          )}

          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="card p-12">
                <div className="w-20 h-20 rounded-full bg-kwk-green/15 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-kwk-green" />
                </div>
                <h3 className="text-3xl font-bold text-kwk-ink mb-4">
                  You are In!
                </h3>
                <p className="text-kwk-slate mb-6">
                  Welcome to the KwK Net early access program. We will contact
                  you soon with next steps.
                </p>
                <button
                  onClick={() => {
                    setActiveForm(null);
                    setSubmitted(false);
                    setError("");
                  }}
                  className="px-8 py-3 bg-kwk-cream ring-1 ring-black/5 hover:bg-kwk-sand text-kwk-ink font-bold rounded-xl transition-all"
                >
                  Back to Options
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
        >
          {[
            { value: "Buyer", label: "Propane, orders, chat, pay" },
            { value: "Seller", label: "Products, inventory, vendors" },
            { value: "Driver", label: "Orders, zones, settlement" },
            { value: "Picker", label: "Thoughtful shopping, real connections" },
          ].map((stat) => (
            <div key={stat.label} className="card p-4 text-center">
              <p className="text-2xl font-bold text-kwk-orange">{stat.value}</p>
              <p className="text-sm text-kwk-slate">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
